'use client';

import { useEffect, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { startNavigationLoading } from '@/lib/navigation-loading';

type StaticDynamicRouter = ReturnType<typeof useRouter>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let clientRouter: StaticDynamicRouter | null = null;

/** Binds the App Router for programmatic navigation in dev (see navigateStaticDynamicHref). */
export function bindStaticDynamicRouter(router: StaticDynamicRouter): void {
  clientRouter = router;
}

export function StaticDynamicRouterBinder() {
  const router = useRouter();
  useEffect(() => {
    bindStaticDynamicRouter(router);
  }, [router]);
  return null;
}

function shouldUseFullDocumentNavigation(): boolean {
  return process.env.NODE_ENV !== 'development';
}

function persistWorkspaceIds(href: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const path = href.split('?')[0] || '';
    const parts = path.split('/').filter(Boolean);
    const formIdx = parts.indexOf('forms');
    const formId = formIdx >= 0 ? parts[formIdx + 1] : '';
    if (formId && UUID_PATTERN.test(formId)) {
      sessionStorage.setItem('eh:workspace-form-id', formId);
    }
    const responseIdx = parts.indexOf('responses');
    const responseId = responseIdx >= 0 ? parts[responseIdx + 1] : '';
    if (responseId && UUID_PATTERN.test(responseId)) {
      sessionStorage.setItem('eh:workspace-response-id', responseId);
    }
    const eventIdx = parts.indexOf('events');
    const eventId = eventIdx >= 0 ? parts[eventIdx + 1] : '';
    if (eventId && UUID_PATTERN.test(eventId)) {
      sessionStorage.setItem('eh:workspace-event-id', eventId);
    }
    const activityIdx = parts.indexOf('activities');
    const activityId = activityIdx >= 0 ? parts[activityIdx + 1] : '';
    if (activityId && UUID_PATTERN.test(activityId)) {
      sessionStorage.setItem('eh:workspace-activity-id', activityId);
      if (eventId && UUID_PATTERN.test(eventId)) {
        sessionStorage.setItem('eh:workspace-activity-event-id', eventId);
      }
    }
  } catch {
    // ignore
  }
}

/**
 * Full document navigation for dynamic `/dashboard/events/[eventId]/…` and
 * `/dashboard/forms/[formId]/…` paths.
 *
 * Next.js client transitions under `output: 'export'` can rewrite the real
 * id segment to the build-time `__default` shell in the address bar,
 * which then breaks path resolvers and leaves workspace pages blank.
 *
 * In local dev, prefer client routing so dashboard middleware keeps same-origin cookies.
 */
export function navigateStaticDynamicHref(href: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  persistWorkspaceIds(href);
  if (!shouldUseFullDocumentNavigation() && clientRouter) {
    startNavigationLoading();
    clientRouter.push(href);
    return;
  }
  startNavigationLoading();
  window.location.assign(href);
}

type StaticDynamicLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string;
  prefetch?: boolean;
  children: ReactNode;
};

/** Plain anchor so Next.js does not soft-navigate into the `__default` shell. */
export function StaticDynamicLink({ href, prefetch, children, onClick, ...rest }: StaticDynamicLinkProps) {
  void prefetch;

  const router = useRouter();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    persistWorkspaceIds(href);
    startNavigationLoading();
    if (!shouldUseFullDocumentNavigation()) {
      router.push(href);
      return;
    }
    window.location.assign(href);
  };

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
